import {
  Container,
  Flex,
  Paper,
  Text,
  Badge,
  Anchor,
  Title,
  Box,
  Button,
} from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconWorld,
  IconExternalLink,
  IconArrowLeft,
  IconClipboardList,
  IconEye,
} from "@tabler/icons-react";
import MatchedProfileTab from "@/pages/profile/MatchedPrfofielTab";
import ProfileVerificationStepper from "@/pages/profile-verification/ProfileVerificationStepper";
import StatusBadge from "@/pages/profile-verification/StatusBadge";
import CompanyProfileHeaderLayout from "@/components/shared/AnonymousCompanyProfile/CompanyProfileHeaderLayout";
import { getCompanyWebsiteDisplayLabel } from "@/shared/utils/companyWebsiteDisplay.util";
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import WhatWeOfferCard from "@/components/shared/AnonymousCompanyProfile/WhatWeOfferCard";
import CollaborationIntentBelowWhatWeOffer from "@/components/shared/CollaborationIntentBelowWhatWeOffer";
import { ANONYMIZED_MASK } from "@/shared/utils/anonymizedProfileDisplay";
import { formatCompanyDisplayName } from "@/shared/utils/companyDisplayName.util";

export type QuestionnaireCta = {
  type: "start" | "view";
  onClick: () => void;
};

interface MatchedProfileViewProps {
  company?: any;
  containerSize?: "fluid";
  isOwnProfile?: boolean;
  /** Collaboration workspace: questionnaire action inside the header card */
  questionnaireCta?: QuestionnaireCta | null;
  /**
   * Collaboration workspace overview only: mobile stacks company block then full-width Partner Readiness CTA
   * (reference card UI — pale panel, navy border/shadow, bold title, vertical contact rows).
   */
  mobileCollabWorkspaceCard?: boolean;
  /** Hide the company header paper (parent renders WorkspacePartnerCompanyHeader above tab panels). */
  suppressHeaderCard?: boolean;
  /** Pre-payment / public preview: mask phone and website as XXX. */
  anonymousView?: boolean;
  /** Collaboration workspace overview: pass match record so intent/summary/roles render without the profile sub-route. */
  workspaceCollaboration?: any;
  onDownloadVerificationPdf?: () => void | Promise<void>;
  verificationPdfDownloading?: boolean;
}

export default function MatchedProfileView({
  company: propCompany,
  containerSize = "fluid",
  isOwnProfile = false,
  questionnaireCta = null,
  mobileCollabWorkspaceCard = false,
  suppressHeaderCard = false,
  anonymousView = false,
  workspaceCollaboration,
  onDownloadVerificationPdf,
  verificationPdfDownloading,
}: MatchedProfileViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, hasProfile } = useUserContext();

  const [company, setCompany] = useState<any>(propCompany || null);
  const [fetchedCollaboration, setFetchedCollaboration] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const collaboration = workspaceCollaboration ?? fetchedCollaboration;

  // Check if we're on the collaboration profile route
  const isCollaborationProfile =
    location.pathname.includes("/collaborations/") && location.pathname.includes("/profile");

  useEffect(() => {
    // Only fetch if we don't have company data from props and we're on collaboration profile route
    if (hasProfile === null) return;
    if (!propCompany && isCollaborationProfile && id) {
      const fetchCompanyData = async () => {
        try {
          const collabRecord = await trpc.collaboration.getById.query({ matchId: id });
          setFetchedCollaboration(collabRecord);

          const myCompanyId = userDetails?.company?.id;
          const otherCompanyId =
            collabRecord.requesterCompanyId === myCompanyId
              ? collabRecord.receiverCompanyId
              : collabRecord.requesterCompanyId;

          const companyData = await trpc.company.getById.query({ companyId: otherCompanyId });
          setCompany(companyData);
        } catch (err) {
          console.error("Error fetching company:", err);
          handleTrpcError(err);
        }
      };

      fetchCompanyData();
    }
  }, [id, propCompany, isCollaborationProfile, userDetails?.company?.id, hasProfile]);

  // Fetch logo URL from S3 when company changes
  useEffect(() => {
    if (company?.logo?.id) {
      getFileUrl(company.logo.id).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  const companyDisplayName = useMemo(
    () => formatCompanyDisplayName(company?.legalName, company?.tradeName),
    [company?.legalName, company?.tradeName],
  );

  const viewerIsRequester = Boolean(
    collaboration && userDetails?.company?.id === collaboration.requesterCompanyId
  );

  const showVerificationStatus =
    isOwnProfile && ["UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED"].includes(company?.status ?? "");

  const gtidBadge = company.gtid ? (
    <Badge
      variant="light"
      color="#EEF2F7"
      size="lg"
      radius="sm"
      style={{
        width: "fit-content",
        backgroundColor: "#EEF2F7",
        color: "#374151",
      }}
    >
      {company.gtid}
    </Badge>
  ) : null;

  const websiteNode = anonymousView ? (
    <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
      <IconWorld size={16} color="#6B7280" style={{ flexShrink: 0 }} />
      <Text size="sm" c="slate.7">{ANONYMIZED_MASK}</Text>
    </Flex>
  ) : company.website ? (
    <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
      <IconWorld size={16} color="var(--mantine-color-primary-6)" style={{ flexShrink: 0 }} />
      <Anchor
        href={
          company.website.startsWith("http") ? company.website : `https://${company.website}`
        }
        target="_blank"
        rel="noopener noreferrer"
        td="none"
        style={{ minWidth: 0 }}
        styles={{
          root: {
            "&:hover": {
              textDecoration: "underline",
            },
          },
        }}
      >
        <Flex align="center" gap={6} wrap="wrap">
          <Text size="sm" c="primary.6" style={{ wordBreak: "break-all" }}>
            {getCompanyWebsiteDisplayLabel(company.website)}
          </Text>
          <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
        </Flex>
      </Anchor>
    </Flex>
  ) : null;

  const contactRow = (
    <Flex gap="md" wrap="wrap" align="center">
      <Flex align="center" gap={8} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        {headquartersFlagEmoji ? (
          <Text
            component="span"
            fz={18}
            lh={1}
            role="img"
            aria-label={`${company.headquartersCountry ?? "Country"} flag`}
            style={{ flexShrink: 0 }}
          >
            {headquartersFlagEmoji}
          </Text>
        ) : null}
        <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-word" }}>
          {company.headquartersCity && company.headquartersCountry
            ? `${company.headquartersCity}, ${company.headquartersCountry}`
            : company.headquartersCountry || "N/A"}
        </Text>
      </Flex>
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconPhone size={16} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-word" }}>
          {anonymousView
            ? ANONYMIZED_MASK
            : company.phoneCountryCode && company.phoneNumber
              ? `${company.phoneCountryCode} ${company.phoneNumber}`
              : "N/A"}
        </Text>
      </Flex>
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconMail size={16} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-all" }}>
          {anonymousView ? ANONYMIZED_MASK : company.email || "N/A"}
        </Text>
      </Flex>
      {websiteNode}
    </Flex>
  );

  const headerRightColumn = (questionnaireCta || showVerificationStatus || gtidBadge) && (
    <Flex direction="column" align="center" gap={6} miw={0} style={{ flexShrink: 0 }}>
      {gtidBadge}
      {questionnaireCta && (
        <Button
          variant={questionnaireCta.type === "start" ? "filled" : "default"}
          size="sm"
          radius="md"
          styles={{
            root:
              questionnaireCta.type === "start"
                ? {
                  backgroundColor: "#ffc300",
                  color: "#04305c",
                  border: "1px solid #ffc300",
                  "&:hover": {
                    backgroundColor: "#e6b000",
                    borderColor: "#e6b000",
                  },
                }
                : {
                  backgroundColor: "#ffffff",
                  color: "#04305c",
                  border: "1px solid #04305c",
                  "&:hover": {
                    backgroundColor: "#ebf0ff",
                    borderColor: "#04305c",
                  },
                },
          }}
          leftSection={
            questionnaireCta.type === "start" ? (
              <IconClipboardList size={18} color="#04305c" />
            ) : (
              <IconEye size={18} color="#04305c" />
            )
          }
          onClick={questionnaireCta.onClick}
        >
          {questionnaireCta.type === "start" ? "Open Partner Readiness" : "View Partner Readiness"}
        </Button>
      )}
      {showVerificationStatus && <StatusBadge status={company.status} />}
    </Flex>
  );

  // Safety check - if no company data, show error
  if (!company) {
    return (
      <Container size={containerSize} py="xl">
        <Text>No company data available</Text>
      </Container>
    );
  }

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      {/* Back Button - Only show on collaboration profile route */}
      {isCollaborationProfile && (
        <Box py="sm" px={{ base: "md", sm: "xl" }}>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate(`/collaborations/${id}`)}
          >
            Back to Workspace
          </Button>
        </Box>
      )}

      <Container size={containerSize} pb="xs" px={{ base: suppressHeaderCard ? 0 : "sm", sm: suppressHeaderCard ? 0 : "md" }}>
        {/* Company Header - Shows REAL data */}
        {!suppressHeaderCard ? (
          <Paper
            p={{ base: mobileCollabWorkspaceCard ? "md" : "sm", sm: "md" }}
            radius="lg"
            mb="xl"
            style={{
              border: "1px solid #04305c",
              backgroundColor: "#ebf0ff",
              ...(mobileCollabWorkspaceCard
                ? { boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)", overflow: "hidden" }
                : {}),
            }}
          >
            {/* Mobile: skeleton layout */}
            <Box hiddenFrom="sm" w="100%">
              <CompanyProfileHeaderLayout
                logo={
                  <Flex
                    w={48}
                    h={48}
                    style={{
                      borderRadius: 8,
                      border: "1px solid #04305c",
                      backgroundColor: "#F9FAFB",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                    align="center"
                    justify="center"
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Company Logo"
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                      />
                    ) : (
                      <IconBuilding size={32} color="#6B7280" />
                    )}
                  </Flex>
                }
                title={
                  <Flex align="center" gap="sm" wrap="wrap">
                    <Title
                      order={2}
                      fw={mobileCollabWorkspaceCard ? 700 : 400}
                      fz={mobileCollabWorkspaceCard ? 19 : 18}
                      c={mobileCollabWorkspaceCard ? "#04305c" : undefined}
                      style={{ wordBreak: "break-word", lineHeight: 1.25 }}
                    >
                      {companyDisplayName}
                    </Title>
                    {company.status === "VERIFIED" && (
                      <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                    )}
                  </Flex>
                }
                topActions={
                  gtidBadge || showVerificationStatus ? (
                    <Flex direction="column" align="center" gap={6}>
                      {gtidBadge}
                      {showVerificationStatus ? (
                        <ProfileVerificationStepper status={company.status} compact align="center" compactLayout="column" />
                      ) : null}
                    </Flex>
                  ) : undefined
                }
                country={contactRow}
              />
              {questionnaireCta ? (
                <Button
                  variant={questionnaireCta.type === "start" ? "filled" : "default"}
                  size={mobileCollabWorkspaceCard ? "md" : "sm"}
                  w="100%"
                  maw="100%"
                  mt="md"
                  radius="md"
                  styles={{
                    root:
                      questionnaireCta.type === "start"
                        ? {
                          backgroundColor: "#ffc300",
                          color: "#04305c",
                          border: "1px solid #ffc300",
                          "&:hover": {
                            backgroundColor: "#e6b000",
                            borderColor: "#e6b000",
                          },
                        }
                        : {
                          backgroundColor: "#ffffff",
                          color: "#04305c",
                          border: "1px solid #04305c",
                          "&:hover": {
                            backgroundColor: "#ebf0ff",
                            borderColor: "#04305c",
                          },
                        },
                  }}
                  leftSection={
                    questionnaireCta.type === "start" ? (
                      <IconClipboardList size={18} color="#04305c" />
                    ) : (
                      <IconEye size={18} color="#04305c" />
                    )
                  }
                  onClick={questionnaireCta.onClick}
                >
                  {questionnaireCta.type === "start"
                    ? "Start Questionnaire"
                    : "View Questionnaire"}
                </Button>
              ) : null}
            </Box>

            {/* Desktop: original layout */}
            <Box visibleFrom="sm" w="100%">
              <Flex gap="md" align="flex-start" justify="space-between" wrap="nowrap" w="100%" miw={0}>
                <Flex gap="md" align="flex-start" miw={0} style={{ flex: 1, minWidth: 0 }}>
                  <Flex
                    w={64}
                    h={64}
                    style={{
                      borderRadius: 8,
                      border: "1px solid #04305c",
                      backgroundColor: "#F9FAFB",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                    align="center"
                    justify="center"
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Company Logo"
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                      />
                    ) : (
                      <IconBuilding size={28} color="#6B7280" />
                    )}
                  </Flex>

                  <Flex direction="column" style={{ flex: 1, minWidth: 0 }} gap={4} miw={0}>
                    <Flex align="center" gap="sm" wrap="wrap">
                      <Title order={2} fw={400} fz={20} style={{ wordBreak: "break-word", lineHeight: 1.25 }}>
                        {companyDisplayName}
                      </Title>
                      {company.status === "VERIFIED" && (
                        <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                      )}
                    </Flex>

                    {contactRow}
                  </Flex>
                </Flex>

                {headerRightColumn}
              </Flex>
            </Box>
          </Paper>
        ) : null}

        <WhatWeOfferCard value={company.customerOfferHighlight} />

        {collaboration ? (
          <CollaborationIntentBelowWhatWeOffer
            collaboration={collaboration}
            company={company}
            isRequester={viewerIsRequester}
            viewerCollaborationIntent={userDetails?.company?.collaborationIntent as string[] | undefined}
          />
        ) : null}

        <Box pt="xl">
          <MatchedProfileTab
            company={company}
            anonymousView={anonymousView}
            onDownloadVerificationPdf={onDownloadVerificationPdf}
            verificationPdfDownloading={verificationPdfDownloading}
          />
        </Box>
      </Container>
    </Box>
  );
}
